import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Users, CreditCard, QrCode, LogOut, 
  BarChart3, Settings, Upload, Save, Image
} from "lucide-react";
import AdminPaymentMethods from "../components/admin/AdminPaymentMethods";

const API = '/api';

const AdminPaymentConfig = () => {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    price: 50,
    price_annual: 80,
    upi_vpa: "",
    upi_qr_image_url: "",
    yape_name: "Utsav Sharan",
    yape_phone: "921959407",
    plin_name: "Utsav Sharan",
    plin_phone: "921959407",
    mp_payment_link_url: ""
  });
  const [yapeQrFile, setYapeQrFile] = useState(null);
  const [plinQrFile, setPlinQrFile] = useState(null);
  const [hasYapeQr, setHasYapeQr] = useState(false);
  const [hasPlinQr, setHasPlinQr] = useState(false);
  const [methodHealth, setMethodHealth] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchMethodHealth();
  }, []);

  const fetchMethodHealth = async () => {
    try {
      const r = await axios.get(`${API}/admin/payment-methods/health`);
      setMethodHealth(r.data);
    } catch (_e) {
      setMethodHealth(null);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment-config`);
      setConfig({
        price: response.data.price || 50,
        price_annual: response.data.price_annual || 80,
        upi_vpa: response.data.upi_vpa || "",
        upi_qr_image_url: response.data.upi_qr_image_url || "",
        yape_name: response.data.yape_name || "Utsav Sharan",
        yape_phone: response.data.yape_phone || "921959407",
        plin_name: response.data.plin_name || "Utsav Sharan",
        plin_phone: response.data.plin_phone || "921959407",
        mp_payment_link_url: response.data.mp_payment_link_url || ""
      });
      setHasYapeQr(!!response.data.yape_qr_url);
      setHasPlinQr(!!response.data.plin_qr_url);
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const { price, price_annual, ...editable } = config;
      await axios.put(`${API}/admin/payment-config`, editable);
      toast.success("Configuración guardada");
      fetchMethodHealth();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadQR = async (method, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append("qr_image", file);
    
    try {
      await axios.post(`${API}/admin/payment-config/qr/${method}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`QR de ${method} actualizado`);
      fetchConfig();
      fetchMethodHealth();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Error al subir el QR de ${method}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B2545] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B2545]">
      {/* Sidebar */}


      {/* Mobile Header */}
      

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Métodos de cobro
            </h1>
            <p className="text-slate-400 mt-1">Define con qué medios puede pagar el cliente, a nombre de quién y con qué código QR. Los precios se fijan en «Planes de membresía».</p>
          </div>

          {/* Price Configuration */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Precios de membresía (solo lectura)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* §4 — autoridad comercial ÚNICA: el catálogo publicado */}
              <div className="mb-5 rounded-xl border border-sky-400/25 bg-sky-500/10 p-4"
                   data-testid="price-readonly-notice">
                <p className="text-sm font-semibold text-sky-100">
                  Los precios de membresía se fijan en «Planes de membresía».
                </p>
                <p className="mt-1 text-xs text-sky-200/90">
                  Aquí solo se muestran como referencia. Antes había dos sitios donde escribir el
                  precio y eso podía dejar cifras distintas en la web y en la app. Ahora manda un
                  único catálogo publicado.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-sky-300/80">Plan de 6 meses</p>
                    <p className="text-lg font-bold text-white" data-testid="price-readonly-6m">
                      S/ {config.price}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-sky-300/80">Plan de 12 meses</p>
                    <p className="text-lg font-bold text-white" data-testid="price-readonly-12m">
                      S/ {config.price_annual}
                    </p>
                  </div>
                  <a
                    href="/admin/membership-plans"
                    className="ml-auto rounded-lg bg-sky-500/20 px-3 py-2 text-xs font-semibold text-sky-100 hover:bg-sky-500/30"
                    data-testid="price-goto-plans"
                  >
                    Editar en Planes de membresía
                  </a>
                </div>
              </div>

              {methodHealth?.blocking_issues?.length > 0 && (
                <div className="mb-5 rounded-xl border border-rose-400/30 bg-rose-500/10 p-4"
                     data-testid="payment-method-blockers">
                  <p className="text-sm font-semibold text-rose-100">
                    Hay métodos de cobro que el cliente no podría usar
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-rose-200">
                    {methodHealth.blocking_issues.map((issue, i) => (
                      <li key={i} data-testid={`payment-blocker-${i}`}>· {issue}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-rose-200/80">{methodHealth.unified_qr_note}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300 block">UPI VPA (India)</Label>
                <Input
                  value={config.upi_vpa}
                  onChange={(e) => setConfig({ ...config, upi_vpa: e.target.value })}
                  placeholder="8750123449@pthdfc"
                  className="bg-[#14375F] border-white/15 text-white"
                  data-testid="upi-vpa-input"
                />
                <Label className="text-slate-300 mt-3 block">UPI QR imagen (ruta)</Label>
                <Input
                  value={config.upi_qr_image_url}
                  onChange={(e) => setConfig({ ...config, upi_qr_image_url: e.target.value })}
                  placeholder="/assets/upi-qr-sarthak.png"
                  className="bg-[#14375F] border-white/15 text-white"
                  data-testid="upi-qr-url-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Yape Configuration */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600 font-bold">Y</span>
                </div>
                Configuración de Yape
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre del titular</Label>
                  <Input
                    value={config.yape_name}
                    onChange={(e) => setConfig({ ...config, yape_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="yape-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Número de teléfono</Label>
                  <Input
                    value={config.yape_phone}
                    onChange={(e) => setConfig({ ...config, yape_phone: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="yape-phone-input"
                  />
                </div>
              </div>

              <div className="border-t border-white/15 pt-4">
                <Label className="text-slate-300 mb-3 block">QR de Yape</Label>
                <div className="flex items-start gap-4">
                  {hasYapeQr && (
                    <div className="w-32 h-32 bg-white rounded-lg overflow-hidden">
                      <img 
                        src={`${API}/payment-config/qr/yape?t=${Date.now()}`} 
                        alt="QR Yape" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div 
                      className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 transition-colors"
                      onClick={() => document.getElementById('yape-qr-input').click()}
                    >
                      <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">
                        {yapeQrFile ? yapeQrFile.name : "Click para subir QR de Yape"}
                      </p>
                    </div>
                    <input
                      id="yape-qr-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setYapeQrFile(file);
                          handleUploadQR("yape", file);
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Plin Configuration */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 font-bold">P</span>
                </div>
                Configuración de Plin
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre del titular</Label>
                  <Input
                    value={config.plin_name}
                    onChange={(e) => setConfig({ ...config, plin_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="plin-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Número de teléfono</Label>
                  <Input
                    value={config.plin_phone}
                    onChange={(e) => setConfig({ ...config, plin_phone: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="plin-phone-input"
                  />
                </div>
              </div>

              <div className="border-t border-white/15 pt-4">
                <Label className="text-slate-300 mb-3 block">QR de Plin</Label>
                <div className="flex items-start gap-4">
                  {hasPlinQr && (
                    <div className="w-32 h-32 bg-white rounded-lg overflow-hidden">
                      <img 
                        src={`${API}/payment-config/qr/plin?t=${Date.now()}`} 
                        alt="QR Plin" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div 
                      className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center cursor-pointer hover:border-green-400 transition-colors"
                      onClick={() => document.getElementById('plin-qr-input').click()}
                    >
                      <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">
                        {plinQrFile ? plinQrFile.name : "Click para subir QR de Plin"}
                      </p>
                    </div>
                    <input
                      id="plin-qr-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setPlinQrFile(file);
                          handleUploadQR("plin", file);
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          {/* Mercado Pago Configuration — iter64 corte 3 */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <span className="text-sky-600 font-bold">MP</span>
                </div>
                Mercado Pago (link de pago)
              </CardTitle>
              <CardDescription className="text-slate-400">
                Link de Mercado Pago Perú al que se envía a los usuarios. Cobra en soles (PEN).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="text-slate-300">URL del link de pago</Label>
                <Input
                  value={config.mp_payment_link_url}
                  onChange={(e) => setConfig({ ...config, mp_payment_link_url: e.target.value })}
                  placeholder="https://link.mercadopago.com.pe/avisocar"
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="mp-link-input"
                />
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleSaveConfig}
            disabled={saving}
            className="w-full h-12 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
            data-testid="save-config-btn"
          >
            {saving ? (
              "Guardando..."
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>

          <AdminPaymentMethods />
        </div>
      </main>
    </div>
  );
};

export default AdminPaymentConfig;
